import {PlatformPressable, useHeaderHeight} from '@react-navigation/elements';
import type {StackScreenProps} from '@react-navigation/stack';
import chroma from 'chroma-js';
import {setStringAsync} from 'expo-clipboard';
import {observer} from 'mobx-react-lite';
import {useCallback, useLayoutEffect} from 'react';
import {StyleSheet, View} from 'react-native';

import NotificationOutlineSvg from '../../../assets/notification_outline.svg';
import {useRoot} from '../../Root';
import type {RootStackParamList} from '../../RootStack/RootStackParamList';
import ShowGroupScreen from '../../screens/ShowGroupScreen';
import {useStyles} from '../../styling';
import {exportSupportGroupSchedule} from '../../SupportGroupScheduleExport';
import useGroup from './useGroup';

export type ShowGroupBindingProps = StackScreenProps<
  RootStackParamList,
  'ShowGroup'
>;

export default observer(function ShowGroupBinding(
  props: ShowGroupBindingProps,
) {
  const {route, navigation} = props;
  const {id: _id} = route.params ?? {};
  const id = parseInt(_id ?? '');

  const root = useRoot();

  const now = root.currentTime.raw;
  const {city, group, localTime} = useGroup({id, now});

  const onNotificationsPress = useCallback(async () => {
    if (!city || !group) {
      return;
    }
    await exportSupportGroupSchedule(root, now, city, {
      ...group,
      location: {street: group.street, house: group.house},
    });
  }, [city, group, now, root]);

  const onAddressPress = useCallback(async () => {
    if (!group) {
      return;
    }
    await setStringAsync(`${group.street} ${group.house}`);
  }, [group]);

  const styles = useStyles(theme => ({
    headerTitle: {
      ...theme.text('header5'),
      color: theme.palette.textPrimary,
      fontSize: 20,
      lineHeight: 35,
    },
    headerBackgroundContainer: {
      backgroundColor: chroma(theme.palette.background).alpha(0.6).hex('rgba'),
    },
  }));

  useLayoutEffect(() => {
    if (!group) {
      return;
    }
    navigation.setOptions({
      headerTitle: group.name,
      headerShown: true,
      headerTitleStyle: styles.headerTitle,
      headerTitleAlign: 'center',
      headerBackgroundContainerStyle: styles.headerBackgroundContainer,
      headerRight:
        group.events.length > 0
          ? _props => {
              const {pressColor, pressOpacity, tintColor} = _props;
              return (
                <PlatformPressable
                  onPress={onNotificationsPress}
                  pressColor={pressColor}
                  pressOpacity={pressOpacity}>
                  <View style={layoutStyles.headerRightIcon}>
                    <NotificationOutlineSvg color={tintColor} />
                  </View>
                </PlatformPressable>
              );
            }
          : undefined,
    });
  }, [group, navigation, onNotificationsPress, styles]);

  const headerHeight = useHeaderHeight();

  return (
    <ShowGroupScreen
      {...group}
      compensateHeaderHeight={headerHeight}
      localTime={localTime}
      onAddressPress={onAddressPress}
    />
  );
});

const layoutStyles = StyleSheet.create({
  headerRightIcon: {
    marginHorizontal: 24,
  },
});
